import Vue from 'vue'
import Vuex from 'vuex'

import dayjs from 'dayjs'

import b4uSocketModule from './b4uSocketModule'
import * as mutations from './mutation-types'
import * as actions from './action-types'
import createB4uSocketPlugin from './b4uSocketPlugin'
import { io } from 'socket.io-client'
import {
  dashboardUrl,
  defaultLocale,
  iframeAvailableLocales,
  initializedFromScript,
  isLocalhost,
  showFullScreen,
  showSystemMessages,
  socketIoHost,
  socketIoPath,
  socketIoSubpathRouting,
  unleashEnabled
} from '../app.config'
import { getLanguageForSocket } from '@/helpers/language'
import { getParentDomain, getRawParentUrl } from '@/store/util'
import { getDocumentShortNames } from '@/helpers/copilot'

const storedLocaleLanguage = defaultLocale
const storedLocale = defaultLocale.toLowerCase().substring(0, 2)
const storedSocketLanguage = getLanguageForSocket(storedLocale)

let realm_tenant = 'Standard'
global.isRestricted = false

let graphId = 0
const matchPath = window.location.pathname
  .replace(/\/\//g, '/')
  .match(/^\/([^\/]*)\/?([^\/]*).*$/)
if (matchPath) {
  if (matchPath[1]) realm_tenant = matchPath[1]
  if (matchPath[2]) graphId = matchPath[2]
}
const isIFrame = window.location !== window.parent.location

realm_tenant = realm_tenant.charAt(0).toUpperCase() + realm_tenant.slice(1)
let rawParentUrl = getRawParentUrl()
let parentUrl = getParentDomain()

const origin =
  isIFrame && !isLocalhost && parentUrl !== dashboardUrl ? parentUrl : '*'
// Either use subpaths or subdomains fro  routing to different socket.io endpoints
// Depends on the platform and is configured uniformly in the deployment

const socket =
  socketIoSubpathRouting === '1'
    ? io(socketIoHost, {
        path: `/agent/${getLanguageForSocket(
          storedSocketLanguage
        )}${socketIoPath}`,
        autoConnect: false,
        timeout: 5000,
        query: {
          TENANT_REALM: realm_tenant,
          PARENT_ORIGIN_URL: origin,
          PARENT_ORIGIN_URL_RAW: rawParentUrl,
          GRAPH_ID: graphId
        },
        transports: ['websocket', 'polling']
      })
    : io(
        socketIoHost.startsWith('http')
          ? `${socketIoHost}`.replace(
              /http(s?):\/\//,
              `http$1://${getLanguageForSocket(storedSocketLanguage)}-`
            )
          : `${getLanguageForSocket(storedSocketLanguage)}-${socketIoHost}`,
        {
          path: socketIoPath,
          autoConnect: false,
          timeout: 5000,
          query: {
            TENANT_REALM: realm_tenant,
            PARENT_ORIGIN_URL: origin,
            PARENT_ORIGIN_URL_RAW: rawParentUrl,
            GRAPH_ID: graphId
          },
          transports: ['websocket', 'polling']
        }
      )

export const setSocketPath = locale => {
  const socketLanguage = getLanguageForSocket(locale)
  socketIoSubpathRouting === '1'
    ? (socket.io.opts.path = `/agent/${socketLanguage}${socketIoPath}`)
    : (socket.io.uri = socketIoHost.startsWith('http')
        ? `${socketIoHost}`.replace(
            /http(s?):\/\//,
            `http$1://${socketLanguage}-`
          )
        : `${socketLanguage}-${socketIoHost}`)
}

export default () => {
  socket.connect()
  const b4uSocketPlugin = createB4uSocketPlugin(socket)

  Vue.use(Vuex)
  return new Vuex.Store({
    plugins: [b4uSocketPlugin],
    state: {
      isDataprocessingConsentAccepted: true, // TODO: reactivate
      isChatOpen:
        window.location.origin === parentUrl ||
        parentUrl === dashboardUrl ||
        !initializedFromScript,
      isRobotOpen: false,
      isUserLoggedIn: false,
      isMaximized: showFullScreen,
      canStartConversation: true,
      isStartConversation: false,
      userInfo: {},
      showSystemMessages: showSystemMessages,
      editMessage: null,
      locale: storedLocaleLanguage, // 'de-DE',
      language: storedLocale, // 'de',
      availableLocales: iframeAvailableLocales,
      userInputText: '',
      inputEnabled: false,
      $socket: socket,
      parentUrl: parentUrl,
      backDisabled: false,
      backInputDisabled: false,
      isExternalIFrame: window.location.origin !== parentUrl,
      isIFrame: isIFrame,
      // external configuration
      chatbotAvatarImageUrl: '',
      headerAvatarImageUrl: '',
      headerText: '',
      shouldShowChatbotAvatarInChat: false,
      shouldUseDifferentAvatarInHeader: false,
      unleashInitialized: !unleashEnabled,
      postMessageTarget: null,
      fileUploadEnabled: false,
      shouldEmitAuthPostMessage: true,
      suggestionsEnabled: true,
      semanticSuggestionsEnabled: true,
      pdfDownloadEnabled: false,
      pdfDownloadLink: false,
      isMobileParent: false,
      showRestartButton: true,
      inputCharacterLimit: null,
      sourceDocumentNames: null,
      documentReferences: null,
      isSmartDocChat: false,
      isCopilotChat: false,
      isAIAgentChat: false,
      lastSmartdocSegmentTooltips: null,
      copilotAiConfig: null,
      smartDocMessageLimitReached: false,
      currentDocumentId: null,
      copilotThemeSet: null,
      isNextGen:
        (new URLSearchParams(window.location.search).get('version') ||
          'default') === 'nextgen'
    },
    actions: {
      [actions.OPEN_ROBOT]: context => {
        context.commit(mutations.OPEN_ROBOT)
      },
      [actions.CLOSE_ROBOT]: context => {
        context.commit(mutations.CLOSE_ROBOT)
      },
      [actions.SET_LANGUAGE]: (context, language) => {
        global.log.info('SET_LANGUAGE', language)
        const shortLang = language.split('-')[0]
        context.commit(mutations.SET_LOCALE, language)
        context.commit(mutations.CLEAR_MESSAGES, language)
        context.commit(mutations.SOCKET_ON_LANGUAGE_CHANGE, null)
        context.dispatch(actions.BOT_DISCONNECT).then(e => e)
        setSocketPath(shortLang)
        sessionStorage.removeItem('currentSession')
        if (navigator.cookieEnabled) {
          localStorage.setItem('localeLanguage', language)
          localStorage.setItem('locale', shortLang)
        }
        context.dispatch(actions.BOT_CONNECT).then(e => e)
      },
      [actions.SET_SPEECH]: (context, speech) => {
        context.commit(mutations.SET_SPEECH, speech)
      },
      [actions.LOG_IN]: context => {
        global.keycloak.loadUserInfo().then(r => {
          context.commit(mutations.LOG_IN, r)
          context.dispatch(actions.EMIT_USER_LOGIN_MESSAGE)
        })
      },
      [actions.EMIT_FAKE_USER_LOGIN_MESSAGE]: context => {},
      [actions.UNLEASH_INIT]: context => {
        context.commit(mutations.UNLEASH_INIT)
      },
      [actions.CLEAR_MESSAGES]: context => {
        context.dispatch(actions.BOT_DISCONNECT)
        context.commit(mutations.CLEAR_MESSAGES)
        context.dispatch(actions.BOT_CONNECT)
      },
      [actions.LOG_OUT]: context => {
        context.commit(mutations.LOG_OUT)
        context.dispatch(actions.EMIT_USER_LOGIN_MESSAGE)
      },
      [actions.OPEN_CHAT]: context => {
        context.dispatch(actions.BOT_CONNECT)
        context.commit(mutations.OPEN_CHAT)
      },
      [actions.CLOSE_CHAT]: ({ commit }) => {
        commit(mutations.CLOSE_CHAT)
      },
      [actions.CLOSE_CHAT_VIA_X]: () => {
        const _message = { message: 'close', type: 'widget' }
        window.parent.postMessage(_message, '*')
      },
      [actions.MAXIMIZE_CHAT]: context => {
        context.commit(mutations.MAXIMIZE_CHAT)
        const _message = { message: 'maximize', type: 'widget' }
        window.parent.postMessage(_message, '*')
      },
      [actions.MINIMIZE_CHAT]: context => {
        context.commit(mutations.MINIMIZE_CHAT)
        const _message = { message: 'minimize', type: 'widget' }
        window.parent.postMessage(_message, '*')
      },
      [actions.PLATFORM_MAXIMIZE_CHAT]: context => {
        context.commit(mutations.MAXIMIZE_CHAT)
      },
      [actions.PLATFORM_MINIMIZE_CHAT]: context => {
        context.commit(mutations.MINIMIZE_CHAT)
      },
      [actions.OPEN_WIDGET]: () => {
        const _message = { message: 'open', type: 'widget' }
        window.parent.postMessage(_message, '*')
      },
      [actions.START_CONVERSATION]: context => {
        context.commit(actions.START_CONVERSATION)
        context.commit(mutations.START_CONVERSATION)
      },
      [actions.CLOSE_CONVERSATION]: context => {
        context.commit(actions.CLOSE_CONVERSATION)
        context.commit(mutations.CLOSE_CONVERSATION)
      },
      [actions.ACCEPTED_INVITATION]: context => {},
      [actions.DECLINED_INVITATION]: context => {},
      [actions.CLICKED_TRIGGER_BUTTON]: context => {},
      [actions.GET_DEVICE_INFO]: context => {
        let data = { type: 'device_info', message: 'getDeviceInfo' }
        window.parent.postMessage(data, '*')
      },
      [actions.SET_DEVICE_INFO]: context => {},

      [actions.SET_INPUT_ENABLED]: (context, inputEnabled) => {
        context.commit(mutations.SET_INPUT_ENABLED, inputEnabled)
      },
      [actions.IS_RESTRICTED]: (context, payload) => {
        context.commit(mutations.IS_RESTRICTED, payload)
      },
      [actions.SET_BACK_DISABLED]: (context, backDisabled) => {
        context.commit(mutations.SET_BACK_DISABLED, backDisabled)
        if (!backDisabled)
          context.commit(mutations.SET_BACK_INPUT_DISABLED, false)
      },
      [actions.SET_BACK_INPUT_DISABLED]: (context, backInputDisabled) => {
        context.commit(mutations.SET_BACK_INPUT_DISABLED, backInputDisabled)
      },
      [actions.SET_CONFIG]: (context, config) => {
        context.commit(mutations.SET_CONFIG, config)
      },
      [actions.SET_DOCUMENT_VIEW_CONFIG]: (context, config) => {
        context.commit(mutations.SET_DOCUMENT_VIEW_CONFIG, config)
      },
      [actions.SET_COPILOT_CHAT]: (context, config) => {
        context.commit(mutations.SET_COPILOT_CHAT, config)
      },
      [actions.SET_COPILOT_AI_CONFIG]: (context, config) => {
        context.commit(mutations.SET_COPILOT_AI_CONFIG, config)
      },
      [actions.SET_AI_AGENT_CHAT]: (context, config) => {
        context.commit(mutations.SET_AI_AGENT_CHAT, config)
      },
      [actions.IS_MESSAGE_LIMIT_REACHED]: (context, messageLimitReached) => {
        context.commit(mutations.IS_MESSAGE_LIMIT_REACHED, messageLimitReached)
      },
      [actions.SET_CURRENT_DOCUMENT_ID]: (context, documentId) => {
        context.commit(mutations.SET_CURRENT_DOCUMENT_ID, documentId)
      },
      [actions.SET_THEME]: (context, theme) => {
        if (
          theme.hasOwnProperty('config') &&
          theme['config'].hasOwnProperty('userInputShouldAllowPdfDownload')
        ) {
          context.commit(
            mutations.SET_PDF_DOWNLOAD_ENABLED,
            theme['config']['userInputShouldAllowPdfDownload']
          )
        }

        // Handle version property from theme config (version === "nextgen")
        if (
          theme.hasOwnProperty('config') &&
          theme['config'].hasOwnProperty('version')
        ) {
          const isNextGenVersion = theme['config']['version'] === 'nextgen'
          context.commit(mutations.SET_NEXTGEN, isNextGenVersion)
        }

        context.commit(mutations.SET_THEME, theme)
      },
      [actions.SET_FILE_UPLOAD_ENABLED]: (context, fileUploadEnabled) => {
        context.commit(mutations.SET_FILE_UPLOAD_ENABLED, fileUploadEnabled)
      },
      [actions.SET_SEMANTIC_SUGGESTIONS_ENABLED]: (context, enabled) => {
        if (context.state.isNextGen) {
          context.commit(mutations.SET_SEMANTIC_SUGGESTIONS_ENABLED, false)
        } else {
          context.commit(mutations.SET_SEMANTIC_SUGGESTIONS_ENABLED, enabled)
        }
      },
      [actions.SET_SUGGESTIONS_ENABLED]: (context, enabled) => {
        context.commit(mutations.SET_SUGGESTIONS_ENABLED, enabled)
      },
      [actions.GET_CONVERSATION_PDF]: context => {
        context.commit(mutations.SET_CONVERSATION_PDF_REQUESTED, 'pending')
      },
      [actions.SET_PDF_DOWNLOAD_ENABLED]: (context, enabled) => {
        context.commit(mutations.SET_PDF_DOWNLOAD_ENABLED, enabled)
      },
      [actions.SMART_DOC]: (context, data) => {
        context.commit(mutations.SMART_DOC, data)
      },
      [actions.SET_COPILOT_THEME]: (context, data) => {
        context.commit(mutations.SET_COPILOT_THEME, data)
      },
      [actions.SET_NEXTGEN]: (context, isNextGen) => {
        context.commit(mutations.SET_NEXTGEN, isNextGen)
      }
    },
    mutations: {
      [mutations.LOG_IN]: (state, userInfo) => {
        state.isUserLoggedIn = true
        state.userInfo = userInfo
      },
      [mutations.LOG_OUT]: state => {
        state.isUserLoggedIn = false
        state.userInfo = {}
      },
      [mutations.UNLEASH_INIT]: state => {
        state.unleashInitialized = true
      },
      [mutations.TOGGLE_SHOW_SYSTEM_MESSAGES]: state => {
        state.showSystemMessages = !state.showSystemMessages
      },
      [mutations.SET_USER_INPUT_TEXT]: (state, userInputText) => {
        state.userInputText = userInputText
      },
      [mutations.ACCEPT_DATA_PROCESSING_CONSENT_DIALOG]: state => {
        state.isDataprocessingConsentAccepted = true
      },
      [mutations.SET_LOCALE]: (state, locale) => {
        state.locale = locale
        state.language = locale.toLowerCase().substring(0, 2)
        if (state.language === 'en') {
          dayjs.locale('en-gb')
        } else {
          dayjs.locale(state.language)
        }
      },
      [mutations.SET_EDIT_MESSAGE]: (state, message) => {
        state.editMessage = message
      },
      [mutations.OPEN_ROBOT]: state => {
        state.isRobotOpen = true
      },
      [mutations.CLOSE_ROBOT]: state => {
        state.isRobotOpen = false
      },
      [mutations.OPEN_CHAT]: state => {
        state.isChatOpen = true
      },
      [mutations.CLOSE_CHAT]: state => {
        state.isChatOpen = false
      },
      [mutations.MAXIMIZE_CHAT]: state => {
        state.isMaximized = true
      },
      [mutations.MINIMIZE_CHAT]: state => {
        state.isMaximized = false
      },
      [mutations.START_CONVERSATION]: state => {
        state.canStartConversation = true
      },
      [mutations.CLOSE_CONVERSATION]: state => {
        state.canStartConversation = false
      },
      [mutations.STARTED_CONVERSATION]: state => {
        state.isStartConversation = true
      },
      [mutations.CLOSED_CONVERSATION]: state => {
        state.isStartConversation = false
      },
      [mutations.SET_INPUT_ENABLED]: (state, inputEnabled) => {
        state.inputEnabled = inputEnabled
      },
      [mutations.SET_BACK_DISABLED]: (state, backDisabled) => {
        state.backDisabled = backDisabled
      },
      [mutations.SET_BACK_INPUT_DISABLED]: (state, backInputDisabled) => {
        state.backInputDisabled = backInputDisabled
      },
      [mutations.SET_CONFIG]: (state, config) => {
        Object.keys(config).forEach(e => {
          if (e in state) state[e] = config[e]
        })
      },

      [mutations.SET_THEME]: (state, theme) => {
        let config = theme['config']
        Object.keys(config).forEach(e => {
          if (e in state) state[e] = config[e]
        })

        let root = document.documentElement
        Object.keys(config).forEach(k => {
          root.style.setProperty(`--${k}`, config[k])
          if (k === 'userInputStrokeColor') {
            root.style.setProperty(`--v-primary-base`, config[k])
            root.style.setProperty(`--v-error-base`, config[k])
          }
        })

        let data = { type: 'setTheme', config: config }
        window.parent.postMessage(data, '*')

        if (state.isCopilotChat && !state.copilotThemeSet) {
          let data = { message: 'configThemeSet' }
          window.parent.postMessage(data, '*')
        }
      },
      [mutations.SET_FILE_UPLOAD_ENABLED]: (state, fileUploadEnabled) => {
        state.fileUploadEnabled = fileUploadEnabled
      },
      [mutations.SET_SEMANTIC_SUGGESTIONS_ENABLED]: (state, enabled) => {
        if (!enabled) state.socket.lastMessageSemanticSuggestions = null
        state.semanticSuggestionsEnabled = enabled
      },
      [mutations.SET_SUGGESTIONS_ENABLED]: (state, enabled) => {
        state.suggestionsEnabled = enabled
      },
      [mutations.SET_CONVERSATION_PDF_REQUESTED]: (state, requestedPDF) => {
        state.pdfDownloadLink = requestedPDF
      },
      [mutations.SET_PDF_DOWNLOAD_ENABLED]: (state, enabled) => {
        state.pdfDownloadEnabled = enabled
      },
      [mutations.SET_PARENT_WINDOW_SIZE]: (state, mobile) => {
        state.isMobileParent = mobile
      },
      [mutations.SET_DOCUMENT_VIEW_CONFIG]: (state, config) => {
        state.inputCharacterLimit = config.character_limit
        state.showRestartButton = config.restart_button
        state.sourceDocumentNames = config.documents
        state.sourceDocumentShortNames = getDocumentShortNames(config.documents)
      },
      [mutations.SET_COPILOT_CHAT]: (state, config) => {
        state.isCopilotChat = config
      },
      [mutations.SET_COPILOT_AI_CONFIG]: (state, config) => {
        state.copilotAiConfig = config
      },
      [mutations.SET_AI_AGENT_CHAT]: (state, config) => {
        state.isAIAgentChat = config
      },
      [mutations.SET_DOCUMENT_REFERENCES]: (state, documentIdReferences) => {
        state.documentReferences = documentIdReferences
      },
      [mutations.IS_MESSAGE_LIMIT_REACHED]: (state, messageLimitReached) => {
        state.smartDocMessageLimitReached = messageLimitReached
      },
      [mutations.SET_CURRENT_DOCUMENT_ID]: (state, documentId) => {
        state.currentDocumentId = documentId
      },
      [mutations.SMART_DOC]: (state, data) => {
        state.isSmartDocChat = data
      },
      [mutations.SET_COPILOT_THEME]: (state, data) => {
        state.copilotThemeSet = data
      },
      [mutations.SET_LAST_SMARTDOC_SEGMENT_TOOLTIPS]: (state, data) => {
        state.lastSmartdocSegmentTooltips = data
      },
      [mutations.SET_NEXTGEN]: (state, isNextGen) => {
        state.isNextGen = isNextGen
      }
    },
    modules: {
      socket: b4uSocketModule
    }
  })
}
